import React, { useEffect, useState } from "react";
import { db } from "@/lib/firebase";
import { collection, getDocs, query, where, addDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { UserPlus, Users, Mail, Phone, MapPin, Calendar, User, Edit3, Trash2 } from "lucide-react";
import MessageDialog from "@/components/ui/MessageDialog";

interface Instructor {
  id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
  address?: string;
  dateOfBirth?: string;
  gender?: string;
  role: string;
  createdAt: any;
  uid?: string;
}

const Instructors: React.FC = () => {
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [message, setMessage] = useState({ open: false, text: "", type: "info" as "info" | "success" | "error" });
  
  // New instructor form state
  const [newInstructor, setNewInstructor] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    address: "",
    dateOfBirth: "",
    gender: "",
  });

  // Edit instructor form state
  const [editingInstructor, setEditingInstructor] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    address: "",
    dateOfBirth: "",
    gender: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch all instructors
        const instructorsSnap = await getDocs(
          query(collection(db, "users"), where("role", "==", "instructor"))
        );
        console.log("Fetched instructors:", instructorsSnap.docs.length);
        setInstructors(instructorsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Instructor)));
      } catch (error) {
        console.error("Error fetching data:", error);
        setMessage({ open: true, text: "Failed to load data.", type: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCreateInstructor = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newInstructor.firstName?.trim() || !newInstructor.lastName?.trim() || !newInstructor.phone?.trim()) {
      setMessage({ open: true, text: "First name, last name, and phone are required.", type: "error" });
      return;
    }

    setCreating(true);
    try {
      // Create unique instructor ID
      const instructorId = `instructor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const instructorData = {
        ...newInstructor,
        createdAt: new Date().toISOString(),
        role: "instructor",
        uid: instructorId,
      };

      console.log("Creating instructor with data:", instructorData);
      const docRef = await addDoc(collection(db, "users"), instructorData);
      console.log("Instructor created with ID:", docRef.id);

      // Add to local state
      setInstructors(prev => [...prev, { id: docRef.id, ...instructorData } as Instructor]);

      setMessage({ open: true, text: "Instructor created successfully!", type: "success" });
      setShowCreateDialog(false);
      setNewInstructor({
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        address: "",
        dateOfBirth: "",
        gender: "",
      });
    } catch (error) {
      console.error("Error creating instructor:", error);
      setMessage({ open: true, text: "Failed to create instructor.", type: "error" });
    } finally {
      setCreating(false);
    }
  };

  const handleEditInstructor = (instructor: Instructor) => {
    setEditingId(instructor.id);
    setEditingInstructor({
      firstName: instructor.firstName,
      lastName: instructor.lastName,
      phone: instructor.phone,
      email: instructor.email || "",
      address: instructor.address || "",
      dateOfBirth: instructor.dateOfBirth || "",
      gender: instructor.gender || "",
    });
    setShowEditDialog(true);
  };

  const handleUpdateInstructor = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingInstructor.firstName?.trim() || !editingInstructor.lastName?.trim() || !editingInstructor.phone?.trim()) {
      setMessage({ open: true, text: "First name, last name, and phone are required.", type: "error" });
      return;
    }

    if (!editingId) return;

    setUpdating(true);
    try {
      const instructorData = {
        ...editingInstructor,
        updatedAt: new Date().toISOString(),
      };

      await updateDoc(doc(db, "users", editingId), instructorData);

      // Update local state
      setInstructors(prev => prev.map(instructor => 
        instructor.id === editingId 
          ? { ...instructor, ...instructorData }
          : instructor
      ));

      setMessage({ open: true, text: "Instructor updated successfully!", type: "success" });
      setShowEditDialog(false);
      setEditingId(null);
      setEditingInstructor({
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        address: "",
        dateOfBirth: "",
        gender: "",
      });
    } catch (error) {
      console.error("Error updating instructor:", error);
      setMessage({ open: true, text: "Failed to update instructor.", type: "error" });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteInstructor = async (id: string) => {
    try {
      await deleteDoc(doc(db, "users", id));
      setInstructors(prev => prev.filter(instructor => instructor.id !== id));
      setMessage({ open: true, text: "Instructor deleted successfully!", type: "success" });
    } catch (error) {
      console.error("Error deleting instructor:", error);
      setMessage({ open: true, text: "Failed to delete instructor.", type: "error" });
    }
  };

  const filteredInstructors = instructors.filter(instructor => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const fullName = `${instructor.firstName} ${instructor.lastName}`.toLowerCase();
    const email = instructor.email?.toLowerCase() || "";
    const phone = instructor.phone?.toLowerCase() || "";
    
    return fullName.includes(searchLower) || 
           email.includes(searchLower) || 
           phone.includes(searchLower);
  });

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Not specified";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Instructors</h1>
          <p className="text-muted-foreground">
            Manage all instructors (available across all branches)
          </p>
        </div>
        
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button>
              <UserPlus className="h-4 w-4 mr-2" />
              Add Instructor
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Instructor</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateInstructor} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    value={newInstructor.firstName}
                    onChange={(e) => setNewInstructor({ ...newInstructor, firstName: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={newInstructor.lastName}
                    onChange={(e) => setNewInstructor({ ...newInstructor, lastName: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  value={newInstructor.phone}
                  onChange={(e) => setNewInstructor({ ...newInstructor, phone: e.target.value })}
                  placeholder="+63123456789"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newInstructor.email}
                  onChange={(e) => setNewInstructor({ ...newInstructor, email: e.target.value })}
                  placeholder="instructor@example.com"
                />
              </div>
              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={newInstructor.address}
                  onChange={(e) => setNewInstructor({ ...newInstructor, address: e.target.value })}
                  placeholder="Complete address"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="dateOfBirth">Date of Birth</Label>
                  <Input
                    id="dateOfBirth"
                    type="date"
                    value={newInstructor.dateOfBirth}
                    onChange={(e) => setNewInstructor({ ...newInstructor, dateOfBirth: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="gender">Gender</Label>
                  <Select value={newInstructor.gender} onValueChange={(value) => setNewInstructor({ ...newInstructor, gender: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={creating} className="flex-1">
                  {creating ? "Creating..." : "Create Instructor"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Instructor Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Instructor</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateInstructor} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-firstName">First Name *</Label>
                  <Input
                    id="edit-firstName"
                    value={editingInstructor.firstName}
                    onChange={(e) => setEditingInstructor({ ...editingInstructor, firstName: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit-lastName">Last Name *</Label>
                  <Input
                    id="edit-lastName"
                    value={editingInstructor.lastName}
                    onChange={(e) => setEditingInstructor({ ...editingInstructor, lastName: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="edit-phone">Phone *</Label>
                <Input
                  id="edit-phone"
                  value={editingInstructor.phone}
                  onChange={(e) => setEditingInstructor({ ...editingInstructor, phone: e.target.value })}
                  placeholder="+63123456789"
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editingInstructor.email}
                  onChange={(e) => setEditingInstructor({ ...editingInstructor, email: e.target.value })}
                  placeholder="instructor@example.com"
                />
              </div>
              <div>
                <Label htmlFor="edit-address">Address</Label>
                <Input
                  id="edit-address"
                  value={editingInstructor.address}
                  onChange={(e) => setEditingInstructor({ ...editingInstructor, address: e.target.value })}
                  placeholder="Complete address"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-dateOfBirth">Date of Birth</Label>
                  <Input
                    id="edit-dateOfBirth"
                    type="date"
                    value={editingInstructor.dateOfBirth}
                    onChange={(e) => setEditingInstructor({ ...editingInstructor, dateOfBirth: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-gender">Gender</Label>
                  <Select value={editingInstructor.gender} onValueChange={(value) => setEditingInstructor({ ...editingInstructor, gender: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={updating} className="flex-1">
                  {updating ? "Updating..." : "Update Instructor"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Users className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search instructors by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Instructors List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-muted-foreground">Loading instructors...</div>
        </div>
      ) : filteredInstructors.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm ? "No instructors found" : "No instructors yet"}
            </h3>
            <p className="text-muted-foreground mb-4 text-center">
              {searchTerm 
                ? "Try adjusting your search criteria." 
                : "Add your first instructor to get started."
              }
            </p>
            {!searchTerm && (
              <Button onClick={() => setShowCreateDialog(true)}>
                <UserPlus className="h-4 w-4 mr-2" />
                Add Instructor
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredInstructors.map((instructor) => (
            <Card key={instructor.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {instructor.firstName} {instructor.lastName}
                    </CardTitle>
                    <Badge variant="secondary" className="text-xs">
                      Instructor
                    </Badge>
                  </div>
                  <div className="flex space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditInstructor(instructor)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit3 className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Instructor</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{instructor.firstName} {instructor.lastName}"? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDeleteInstructor(instructor.id)} 
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{instructor.phone}</span>
                </div>
                
                {instructor.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="truncate">{instructor.email}</span>
                  </div>
                )}
                
                {instructor.address && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{instructor.address}</span>
                  </div>
                )}
                
                {instructor.dateOfBirth && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Born: {formatDate(instructor.dateOfBirth)}</span>
                  </div>
                )}
                
                {instructor.gender && (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground capitalize">{instructor.gender}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Message Dialog */}
      <MessageDialog
        open={message.open}
        onClose={() => setMessage({ ...message, open: false })}
        title={message.type === "error" ? "Error" : message.type === "success" ? "Success" : "Information"}
        message={message.text}
        type={message.type}
      />
    </div>
  );
};

export default Instructors;
